<template lang="pug">
  b-modal(
    v-model="show",
    size="lg",
    header-class="lmm-common-popup-header",
    dialog-class="mdm-app-policy-dialog",
    :title="title",
    @hidden="reset")
    //- 구성+companywide 매트릭스 로딩 중 (앱별 N+1 조회라 잠깐 걸릴 수 있음)
    div.text-center.py-4(v-if="loading")
      b-spinner(small)
    template(v-else)
      p.text-muted.small.mb-2 {{$t('mdm_page.install_policy_help')}}
      b-table.list-stacked.mdm-policy-table(
        :fields="fields",
        :items="rows",
        show-empty,
        :empty-text="$t('mdm_page.no_config')",
        stacked="md")
        //- 구성 이름 — companywide(전체 적용)는 뱃지로 구분
        template(v-slot:cell(name)="row")
          span {{row.item.name}}
          b-badge.ml-1(v-if="row.item.isCompanywide", variant="info") {{$t('mdm_page.companywide_badge')}}
        //- 설치 정책 — 행마다 3택 드롭다운(설치함/설치 안 함/삭제)
        template(v-slot:cell(state)="row")
          b-form-select.mdm-app-policy-state(v-model="row.item.value", :options="stateOptions", size="sm")
    template(slot="modal-footer")
      b-btn(size="sm", variant="primary", :disabled="saving || loading", @click="submit") {{$t('save')}}
      b-btn(size="sm", variant="secondary", :disabled="saving", @click="show = false") {{$t('cancel')}}
</template>

<script>
// 앱 설치 정책 모달 — "이 앱을 어느 구성에 어떻게 적용할지"를 한 화면에서 설정(앱 중심).
// 상태 3종: 설치함(INSTALL) / 설치 안 함(매트릭스에 행 없음) / 삭제(UNINSTALL).
// 조회·저장 모두 앱 기준 단일 엔드포인트로 처리: GET/PUT /mdm/applications/{pk}/profiles
// (조회=전 구성+현재 action 한 번에, 저장=변경분만 벌크 한 번에 — 구성별 개별 호출 없음).
import { MdmAppAPI } from "@/api/mdm"

export default {
  name: "MdmAppInstallPolicyModal",
  data() {
    return {
      show: false,
      loading: false,
      saving: false,
      platform: "windows",
      app: null,
      rows: [], // [{ key, isCompanywide, pk?, name, original, value }]
      stateOptions: [
        { value: "install", text: this.$t("mdm_page.state_install") },
        { value: "none", text: this.$t("mdm_page.state_none") },
        { value: "uninstall", text: this.$t("mdm_page.state_uninstall") },
      ],
      fields: [
        { key: "name", label: this.$t("mdm_page.config_name"), thStyle: { width: "60%" } },
        { key: "state", label: this.$t("mdm_page.install_policy"), thStyle: { width: "40%" } },
      ],
    }
  },
  computed: {
    title() {
      return this.$t("mdm_page.install_policy_title", { app: this.app ? this.app.app_name : "" })
    },
  },
  methods: {
    // 부모(MdmAppPage)가 ref로 호출 — platform = 현재 탭, app = 대상 앱 객체
    open(platform, app) {
      this.platform = platform
      this.app = app
      this.rows = []
      this.saving = false
      this.show = true
      void this.loadPolicies()
    },
    reset() {
      this.rows = []
      this.app = null
      this.saving = false
      this.loading = false
    },
    // 이 앱의 OS 전 구성 + 각 구성에서의 현재 action을 신규 단일 엔드포인트로 한 번에 조회(N+1 제거)
    async loadPolicies() {
      this.loading = true
      try {
        // GET /mdm/applications/{app_pk}/profiles — 전 구성(없으면 action null) + companywide(없으면 합성행)
        const res = await MdmAppAPI.get(`${this.app.pk}/profiles`)
        const targets = res.data.objects || []
        this.rows = targets.map(t => {
          const state = this.actionToState(t.action) // null → 'none'(설치 안 함)
          return {
            key: t.is_company_wide ? "companywide" : `cfg-${t.profile_fk}`,
            isCompanywide: t.is_company_wide,
            pk: t.profile_fk, // 전체적용 미생성 시 null(프론트는 OS로 저장하므로 무방)
            name: t.is_company_wide ? this.$t("mdm_page.all_configs") : t.name,
            original: state,
            value: state,
          }
        })
        // 전체적용("모든 구성")을 맨 위로 (백엔드 정렬은 일반 구성 기준이라 여기서 고정)
        this.rows.sort((a, b) => (b.isCompanywide ? 1 : 0) - (a.isCompanywide ? 1 : 0))
      } catch (error) {
        // 조회 실패 토스트는 전역 인터셉터가 처리
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    actionToState(action) {
      if (action === "INSTALL") return "install"
      if (action === "UNINSTALL") return "uninstall"
      return "none"
    },
    stateToAction(state) {
      return state === "install" ? "INSTALL" : "UNINSTALL"
    },
    // 변경된 행 → 벌크 body 항목. action: 설치함=INSTALL / 삭제=UNINSTALL / 설치 안 함=null(행 제거)
    buildTarget(row) {
      return {
        profile_fk: row.isCompanywide ? null : row.pk, // 전체적용은 OS로 주소(pk 불필요)
        is_company_wide: row.isCompanywide,
        action: row.value === "none" ? null : this.stateToAction(row.value),
      }
    },
    async submit() {
      if (this.saving) return
      const changes = this.rows.filter(r => r.value !== r.original)
      if (!changes.length) {
        this.show = false
        return
      }
      this.saving = true
      try {
        // 변경분만 단일 PUT으로 한 번에 — 백엔드가 변경 구성 모아 재pull 1묶음(N회 호출 없음)
        await MdmAppAPI.put(`${this.app.pk}/profiles`, {
          targets: changes.map(r => this.buildTarget(r)),
        })
        this.$awn.success(this.$t("mdm_page.install_policy_saved"))
        this.show = false
        this.$emit("policy-saved")
      } catch (error) {
        // 실패 시 전역 인터셉터가 토스트 → 모달 유지해 재시도(벌크 멱등이라 안전)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<!-- 모달은 렌더 시 <body>로 이동해 scoped CSS가 안 닿음 → non-scoped + 고유 클래스 -->
<style lang="scss">
.mdm-app-policy-dialog {
  // 다른 MDM 모달과 동일한 상단 여백 (모바일 표준 §10)
  margin-top: 8vh;

  @media (max-width: 575px) {
    margin-top: 1rem;
  }
}

// 이 모달의 정책 테이블 전용 — 고유 클래스를 테이블에 직접 박아 타겟(조상 의존 X).
// 다른 .list-stacked 목록엔 영향 없음.
.mdm-policy-table {
  // 데스크탑: 상태 드롭다운 고정폭
  .mdm-app-policy-state {
    max-width: 160px;
  }

  // 모바일 stacked 카드: 라벨|값 2열을 유지하되, 칼럼을 행끼리 정렬 + 블록을 카드 가운데로.
  // bootstrap-vue 2.x stacked는 float 기반(td=block, 라벨 ::before float:left 40% / 값 div inline-block 60%).
  // td를 직접 grid로 강제(display:grid)해야 grid-template-columns가 먹는다 — 첫 시도가 안 먹은 이유가 이것.
  // 기본 규칙 specificity가 높아 !important 필요(프레임워크 내부 레이아웃 override — §10 .btn-link와 같은 케이스).
  @media (max-width: 767px) {
    &.b-table-stacked-md > tbody > tr > td {
      display: grid !important;
      grid-template-columns: 6em 1fr; // 라벨(고정폭) | 값 — 두 행의 칼럼이 정렬됨
      align-items: center;
      column-gap: 0.5rem;
      max-width: 340px; // 2열 묶음 폭 제한
      margin-left: auto !important; // 블록을 카드 가운데로
      margin-right: auto !important;

      // 라벨: float 해제, grid 셀 안에서 우측 정렬(가운데 갭 쪽으로)
      &::before {
        float: none !important;
        width: auto !important;
        text-align: right !important;
        padding: 0 !important;
      }

      // 빈 clearfix(::after)가 grid 아이템으로 끼어 칼럼이 틀어짐 → 제거
      &::after {
        display: none !important;
      }

      // 값: inline-block 60% → grid 셀(좌측 정렬)
      > div {
        width: auto !important;
        padding: 0 !important;
        text-align: left;
      }
    }

    // 드롭다운: 값 칼럼 안에서 너무 길지 않게
    .mdm-app-policy-state {
      max-width: 220px;
    }
  }
}
</style>
